import { useCallback, useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import * as forumSvc from '../services/forumService';

const Forum = ({ onGoUpLevel, onGoBackTab }) => {
  const { currentUser, loading: authLoading } = useUser();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [newTopic, setNewTopic] = useState({ title: '', content: '', category: 'Maths' });
  const [newResponse, setNewResponse] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit states
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingResponse, setEditingResponse] = useState(null);
  const [editContent, setEditContent] = useState('');

  const categories = ['Maths', 'Français', 'Administratif', 'Culture Générale', 'Autre'];

  // Load topics - with error handling
  const loadTopics = useCallback(async () => {
    if (!currentUser) {
      setTopics([]);
      setSelectedTopic(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await forumSvc.getTopics({
        category: categoryFilter,
        authorId: activeTab === 'mine' && currentUser ? currentUser.id : undefined,
        sort: sortBy,
      });
      setTopics(data || []);
    } catch (err) {
      console.error('Erreur Forum:', err);
      setError(err.message || 'Impossible de récupérer les discussions. Réessayez.');
      // Keep the last known list on a temporary failure.
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryFilter, sortBy, currentUser]);

  useEffect(() => {
    if (!authLoading) {
      loadTopics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, categoryFilter, sortBy, currentUser, authLoading]);

  // Effet pour gérer le bouton Retour du header (niveaux de navigation)
  useEffect(() => {
    if (onGoUpLevel && onGoBackTab && selectedTopic) {
      // Niveau 2: Retour à la liste des topics
      onGoUpLevel(() => {
        setSelectedTopic(null);
        setEditingTopic(null);
        setEditingResponse(null);
      }, 2);
    }
  }, [selectedTopic, onGoUpLevel, onGoBackTab]);

  // Auto-dismiss messages after 5 seconds (user can also close manually)
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Create new topic
  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setError('');
      await forumSvc.createTopic(newTopic);

      setSuccess('Sujet créé avec succès !');
      setNewTopic({ title: '', content: '', category: 'Maths' });
      setShowNewTopicForm(false);
      loadTopics();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update topic
  const handleUpdateTopic = async (e) => {
    e.preventDefault();
    if (!editingTopic) return;

    try {
      setError('');
      await forumSvc.updateTopic(editingTopic.id, {
        title: editContent.title,
        content: editContent.content,
        category: editContent.category
      });

      setSuccess('Sujet modifié avec succès !');
      setEditingTopic(null);
      loadTopics();
      
      // Refresh selected topic
      const updatedTopic = await forumSvc.getTopicById(selectedTopic.id);
      setSelectedTopic(updatedTopic);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete topic
  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce sujet ?')) return;

    try {
      setError('');
      await forumSvc.deleteTopic(topicId);

      setSuccess('Sujet supprimé avec succès !');
      setSelectedTopic(null);
      loadTopics();
    } catch (err) {
      setError(err.message);
    }
  };

  // Add response
  const handleAddResponse = async (e) => {
    e.preventDefault();
    if (!currentUser || !selectedTopic) return;

    try {
      setError('');
      await forumSvc.createResponse(selectedTopic.id, {
        content: newResponse
      });

      setSuccess('Réponse ajoutée avec succès !');
      setNewResponse('');
      loadTopics();
      
      // Refresh selected topic
      const updatedTopic = await forumSvc.getTopicById(selectedTopic.id);
      setSelectedTopic(updatedTopic);
    } catch (err) {
      setError(err.message);
    }
  };

  // Update response
  const handleUpdateResponse = async (e) => {
    e.preventDefault();
    if (!editingResponse || !selectedTopic) return;

    try {
      setError('');
      await forumSvc.updateResponse(selectedTopic.id, editingResponse.id, { content: editContent });

      setSuccess('Réponse modifiée avec succès !');
      setEditingResponse(null);
      setEditContent('');
      loadTopics();
      
      // Refresh selected topic
      const updatedTopic = await forumSvc.getTopicById(selectedTopic.id);
      setSelectedTopic(updatedTopic);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete response
  const handleDeleteResponse = async (responseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réponse ?')) return;
    if (!selectedTopic) return;

    try {
      setError('');
      await forumSvc.deleteResponse(selectedTopic.id, responseId);

      setSuccess('Réponse supprimée avec succès !');
      loadTopics();
      
      // Refresh selected topic
      const updatedTopic = await forumSvc.getTopicById(selectedTopic.id);
      setSelectedTopic(updatedTopic);
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing topic
  const startEditingTopic = (topic) => {
    setEditingTopic(topic);
    setEditContent({ title: topic.title, content: topic.content, category: topic.category });
  };

  // Start editing response
  const startEditingResponse = (response) => {
    setEditingResponse(response);
    setEditContent(response.content);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `il y a ${diffMins}min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays < 7) return `il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  // Check if user is author
  const isAuthor = (authorId) => {
    return currentUser && currentUser.id === authorId;
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-secondary">Vérification de la connexion...</p>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="space-y-8 cb-forum-page">
        <section className="bg-orange-600 dark:bg-orange-800 p-10 rounded-3xl text-white">
          <h2 className="text-4xl font-extrabold mb-4">Communauté d'Entraide</h2>
          <p className="text-xl text-orange-50 max-w-2xl">
            Le forum est un espace privé réservé aux utilisateurs connectés.
          </p>
        </section>

        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-700 p-6 rounded-2xl text-center">
          <p className="text-amber-950 dark:text-amber-100 font-semibold">
            🔒 Connectez-vous pour accéder aux sujets et aux échanges du forum.
          </p>
        </div>
      </div>
    );
  }

  if (selectedTopic) {
    return (
      <div className="space-y-6 cb-forum-page">
        {/* Messages - Toast style floating notification */}
        {error && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg flex justify-between items-center max-w-sm">
            <span>{error}</span>
            <button type="button" onClick={loadTopics} className="ml-3 underline font-semibold">Réessayer</button>
            <button onClick={() => setError('')} className="text-red-700 font-bold ml-4 hover:text-red-900">✕</button>
          </div>
        )}
        {success && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex justify-between items-center max-w-sm">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-700 font-bold ml-4 hover:text-green-900">✕</button>
          </div>
        )}

        {/* Topic details */}
        <div className="surface-card p-8 rounded-2xl shadow-sm border border-color">
          {editingTopic ? (
            <form onSubmit={handleUpdateTopic} className="space-y-4">
              <h3 className="font-bold text-lg mb-4">Modifier le sujet</h3>
              
              <div>
                <label className="block text-sm text-secondary mb-1">Catégorie</label>
                <select
                  value={editContent.category}
                  onChange={(e) => setEditContent({ ...editContent, category: e.target.value })}
                  className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-secondary mb-1">Titre</label>
                <input
                  type="text"
                  value={editContent.title}
                  onChange={(e) => setEditContent({ ...editContent, title: e.target.value })}
                  className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-secondary mb-1">Description</label>
                <textarea
                  value={editContent.content}
                  onChange={(e) => setEditContent({ ...editContent, content: e.target.value })}
                  className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                  rows="5"
                  required
                />
              </div>

              <div className="flex gap-2">
                <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl">
                  ✓ Sauvegarder
                </button>
                <button type="button" onClick={() => setEditingTopic(null)} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl">
                  ✕ Annuler
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                  {selectedTopic.category}
                </span>
                <span className="text-sm text-secondary">
                  {formatDate(selectedTopic.createdAt)}
                </span>
                {isAuthor(selectedTopic.authorId) && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => startEditingTopic(selectedTopic)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteTopic(selectedTopic.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                )}
              </div>
              
              <h1 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4">
                {selectedTopic.title}
              </h1>
              
              <p className="text-primary whitespace-pre-wrap mb-6">
                {selectedTopic.content}
              </p>
              
              <div className="flex items-center gap-2 text-sm text-secondary border-t border-color pt-4">
                <span>Par</span>
                <span className="font-semibold">{selectedTopic.authorName}</span>
              </div>
            </>
          )}
        </div>

        {/* Responses */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-blue-800 dark:text-blue-400">
            Réponses ({selectedTopic.responses?.length || 0})
          </h2>

          {selectedTopic.responses?.length === 0 ? (
            <p className="text-secondary italic">Aucune réponse pour le moment.</p>
          ) : (
            selectedTopic.responses?.map((response) => (
              <div
                key={response.id}
                className="surface-card p-6 rounded-2xl shadow-sm border border-color"
              >
                {editingResponse?.id === response.id ? (
                  <form onSubmit={handleUpdateResponse} className="space-y-3">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
                      rows="4"
                      required
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl text-sm">
                        ✓ Sauvegarder
                      </button>
                      <button type="button" onClick={() => { setEditingResponse(null); setEditContent(''); }} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl text-sm">
                        ✕ Annuler
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{response.authorName}</span>
                        <span className="text-sm text-secondary">
                          {formatDate(response.createdAt)}
                        </span>
                        {isAuthor(response.authorId) && (
                          <div className="flex gap-2 ml-2">
                            <button
                              onClick={() => startEditingResponse(response)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteResponse(response.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-primary">{response.content}</p>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add response form */}
        {currentUser ? (
          <form onSubmit={handleAddResponse} className="surface-card p-6 rounded-2xl shadow-sm border border-color">
            <h3 className="font-bold mb-4">Ajouter une réponse</h3>
            <textarea
              value={newResponse}
              onChange={(e) => setNewResponse(e.target.value)}
              className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
              rows="4"
              placeholder="Votre réponse..."
              required
            />
            <button
              type="submit"
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-xl transition-colors"
            >
              Envoyer la réponse
            </button>
          </form>
        ) : (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl text-center">
            <p className="text-yellow-700 dark:text-yellow-400">
              Connectez-vous pour répondre
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8 cb-forum-page">
      {/* Header Section */}
      <section className="bg-orange-600 dark:bg-orange-800 p-10 rounded-3xl text-white">
        <h2 className="text-4xl font-extrabold mb-4">Communauté d'Entraide</h2>
        <p className="text-xl text-orange-50 max-w-2xl">
          Échangez avec les autres candidats.
          Partagez vos méthodes de révision, posez vos questions et entraidez-vous.
        </p>
      </section>

      {/* Messages - Toast style floating notification */}
      {error && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg flex justify-between items-center max-w-sm">
            <span>{error}</span>
            <button type="button" onClick={loadTopics} className="ml-3 underline font-semibold">Réessayer</button>
            <button onClick={() => setError('')} className="text-red-700 font-bold ml-4 hover:text-red-900">✕</button>
          </div>
        )}
        {success && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex justify-between items-center max-w-sm">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-700 font-bold ml-4 hover:text-green-900">✕</button>
          </div>
        )}

      {/* User Info Banner */}
      {currentUser ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-xl flex items-center justify-between">
          <span className="text-green-700 dark:text-green-400">
            ✓ Connecté en tant que <strong>{currentUser.pseudonym}</strong>
          </span>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-xl">
          <span className="text-yellow-700 dark:text-yellow-400">
            💡 <strong>Connectez-vous</strong> pour poster et interagir avec la communauté
          </span>
        </div>
      )}

      {/* Tabs and filters */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
              activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-secondary hover:bg-blue-100 dark:hover:bg-slate-700'
            }`}
          >
            📋 Tous les sujets
          </button>
          {currentUser && (
            <button
              onClick={() => setActiveTab('mine')}
              className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                activeTab === 'mine'
                  ? 'bg-blue-600 text-white'
                  : 'bg-secondary hover:bg-blue-100 dark:hover:bg-slate-700'
              }`}
            >
              📂 Mes questions
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 items-center">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
          >
            <option value="all">Toutes catégories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
          >
            <option value="newest">Plus récents</option>
            <option value="oldest">Plus anciens</option>
          </select>
        </div>
      </div>

      {/* New topic button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowNewTopicForm(!showNewTopicForm)}
          className={`px-6 py-2 rounded-xl font-semibold transition-all ${
            currentUser
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-300 dark:bg-gray-700 text-secondary cursor-not-allowed'
          }`}
          disabled={!currentUser}
        >
          {showNewTopicForm ? '✕ Annuler' : '+ Nouveau sujet'}
        </button>
      </div>

      {/* New topic form */}
      {showNewTopicForm && (
        <form onSubmit={handleCreateTopic} className="surface-card p-8 rounded-2xl shadow-sm border border-color space-y-4">
          <h3 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-4">
            Créer un nouveau sujet
          </h3>
          
          <div>
            <label className="block text-sm text-secondary mb-1">Catégorie</label>
            <select
              value={newTopic.category}
              onChange={(e) => setNewTopic({ ...newTopic, category: e.target.value })}
              className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-secondary mb-1">Titre</label>
            <input
              type="text"
              value={newTopic.title}
              onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
              className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
              placeholder="Posez votre question..."
              required
            />
          </div>

          <div>
            <label className="block text-sm text-secondary mb-1">Description</label>
            <textarea
              value={newTopic.content}
              onChange={(e) => setNewTopic({ ...newTopic, content: e.target.value })}
              className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
              rows="5"
              placeholder="Décrivez votre question en détail..."
              required
            />
          </div>

          <button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            ✓ Publier le sujet
          </button>
        </form>
      )}

      {/* Topics list */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-secondary">Chargement...</p>
        </div>
      ) : topics.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-secondary italic">Aucun sujet dans cette catégorie.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {topics.map((topic) => (
            <div
              key={topic.id}
              onClick={() => setSelectedTopic(topic)}
              className="surface-card p-6 rounded-2xl shadow-sm border border-color hover:border-blue-300 transition-all group cursor-pointer"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                      {topic.category}
                    </span>
                    <span className="text-sm text-secondary">
                      {formatDate(topic.createdAt)}
                    </span>
                    {isAuthor(topic.authorId) && activeTab === 'all' && (
                      <span className="text-blue-600 text-sm">( vous )</span>
                    )}
                  </div>
                  <h2 className="text-xl font-bold group-hover:text-blue-600 transition-colors mb-2">
                    {topic.title}
                  </h2>
                  <p className="text-secondary text-sm">
                    Par <span className="font-semibold">{topic.authorName}</span>
                  </p>
                </div>
                <div className="text-center bg-secondary px-4 py-2 rounded-xl ml-4">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {topic.responseCount}
                  </p>
                  <p className="text-[10px] uppercase font-bold text-secondary">
                    Réponses
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Forum;
